import { EventLog_BookingKitEventFragment } from "@graphql";
import { CircleWithIcon, Color, DraftSettingsIcon, NewsIcon } from "@bonliva-crm/ui";
import { EventEntry, EventEntryProps } from "../components/event";
import { Description } from "../components/description";
import { FilterType, MainFilterType } from "./filter-type";

type ComponentProps = Partial<EventEntryProps>;

export const createFilteredEventComponents = ({
  filters,
  events,
  onUpdated
}: {
  filters: FilterType[];
  events: Record<FilterType, EventLog_BookingKitEventFragment[]> | null;
  onUpdated: () => void;
}) => {
  if (!events) return [];

  const componentProps: ComponentProps = {
    onUpdated
  };

  const filteredComponents: {
    component: JSX.Element;
    createdAt: Date;
    sticky: boolean;
  }[] = [];

  if (filters.length === 0) {
    filteredComponents.push(
      ...createNoteComponents(events[MainFilterType.Comment], componentProps)
    );
    filteredComponents.push(
      ...createSystemLogComponents(events[MainFilterType.SystemLog], componentProps)
    );
  } else {
    filters.forEach((filter) => {
      switch (filter) {
        case MainFilterType.Comment:
          filteredComponents.push(
            ...createNoteComponents(events[MainFilterType.Comment], componentProps)
          );
          break;

        case MainFilterType.SystemLog:
          filteredComponents.push(
            ...createSystemLogComponents(events[MainFilterType.SystemLog], componentProps)
          );
          break;

        default:
          throw new Error(`Unknown filter type ${filter}`);
      }
    });
  }
  return filteredComponents
    .sort((a, b) => {
      if (a.sticky && !b.sticky) return -1;
      if (!a.sticky && b.sticky) return 1;
      return b.createdAt.getTime() - a.createdAt.getTime();
    })
    .map((e) => e.component);
};

const createNoteComponents = (
  events: EventLog_BookingKitEventFragment[],
  componentProps: ComponentProps
) => {
  return events.map((note) => ({
    component: (
      <EventEntry
        key={note._id}
        icon={
          <CircleWithIcon
            circleColor={Color.Brown}
            icon={<NewsIcon htmlColor={Color.White} style={{ paddingBottom: "1px" }} />}
          />
        }
        event={note}
        content={() => (
          <Description
            dangerouslySetInnerHTML={{ __html: note.DescriptionHtml }}
            reduceVerticalMargins
          />
        )}
        {...componentProps}
      />
    ),
    createdAt: new Date(note.CreatedOn),
    sticky: note.Sticky
  }));
};

const createSystemLogComponents = (
  events: EventLog_BookingKitEventFragment[],
  componentProps: ComponentProps
) => {
  return events.map((systemLog) => ({
    component: (
      <EventEntry
        key={systemLog._id}
        icon={
          <CircleWithIcon circleColor={Color.Information} icon={<DraftSettingsIcon />} />
        }
        event={systemLog}
        content={() => (
          <Description
            dangerouslySetInnerHTML={{ __html: systemLog.DescriptionHtml }}
            reduceVerticalMargins
          />
        )}
        {...componentProps}
      />
    ),
    createdAt: new Date(systemLog.CreatedOn),
    sticky: systemLog.Sticky
  }));
};
